/**
 * Модель графика в браузере: правки применяются сразу и пересчитываются тем же ядром, что на сервере
 * (ПЛ-10), затем уходят на сервер пакетом; отмена и повтор — обратными пакетами (ПЛ-25).
 */
import {
  type ActivityInput,
  type ActivityRow,
  type CalendarDef,
  computeSchedule,
  dayNumber,
  durationFromQuantity,
  type EngineActivity,
  type EngineOptions,
  type EngineRelation,
  formatMoment,
  type RelationRow,
  type ScheduleOp,
  type ScheduleView,
  WorkCalendar,
} from '@epc/contracts';

export type View = ScheduleView;

export function defaultCalId(v: View): string {
  if (v.schedule.calendarId && v.calendars.some((c) => c.id === v.schedule.calendarId))
    return v.schedule.calendarId;
  return v.calendars[0]?.id ?? 'fallback';
}

export function calDef(v: View, id: string | null | undefined): CalendarDef | undefined {
  return v.calendars.find((c) => c.id === (id ?? defaultCalId(v)))?.def as CalendarDef | undefined;
}

export function hpd(v: View, calendarId?: string | null): number {
  return calDef(v, calendarId)?.hoursPerDay ?? calDef(v, null)?.hoursPerDay ?? 8;
}

/** «5 дн», «4 ч», «2,5 дн» */
export function fmtDur(min: number | null | undefined, hoursPerDay: number): string {
  if (min === null || min === undefined) return '—';
  const day = hoursPerDay * 60;
  if (min !== 0 && Math.abs(min) < day && min % day !== 0) {
    const h = Math.round((min / 60) * 10) / 10;
    return `${String(h).replace('.', ',')} ч`;
  }
  const d = Math.round((min / day) * 10) / 10;
  return `${String(d).replace('.', ',')} дн`;
}

/** «19.10.26» или «19.10.26 08:00» */
export function fmtMoment(s: string | null | undefined, withTime = false): string {
  if (!s) return '—';
  const d = `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(2, 4)}`;
  return withTime && s.length >= 16 ? `${d} ${s.slice(11, 16)}` : d;
}

/** Ввод даты (ГГГГ-ММ-ДД) и времени → момент ядра */
export function toMoment(day: string, time = '00:00'): string {
  return `${day}T${time}`;
}

/** Входные данные ядра из вида: как на сервере */
export function engineInput(v: View): {
  acts: EngineActivity[];
  rels: EngineRelation[];
  opts: EngineOptions;
} {
  const s = v.schedule;
  const calendars: Record<string, CalendarDef> = {};
  for (const c of v.calendars) calendars[c.id] = c.def as CalendarDef;
  const def = defaultCalId(v);
  const h = hpd(v);
  const acts: EngineActivity[] = v.activities.map((a) => ({
    id: a.id,
    type: a.type,
    calendarId: a.calendarId && calendars[a.calendarId] ? a.calendarId : def,
    duration: a.durationMin,
    remaining: a.remainingMin,
    actualStart: a.actualStart,
    actualFinish: a.actualFinish,
    percent: a.percent,
    constraint: a.constraintType,
    constraintDate: a.constraintDate,
    deadline: a.deadline,
  }));
  for (const x of v.external)
    acts.push({ id: x.id, type: 'task', duration: 0, external: { start: x.start, finish: x.finish } });
  const rels: EngineRelation[] = v.relations.map((r) => ({
    id: r.id,
    predId: r.predId,
    succId: r.succId,
    type: r.type,
    lag: r.lagMin,
    lagPct: r.lagUnit === 'pct' ? r.lagPct : null,
    lagCalendar: r.lagCalendar,
  }));
  return {
    acts,
    rels,
    opts: {
      projectStart: s.startDate,
      dataDate: s.dataDate,
      mustFinishBy: s.mustFinishBy,
      outOfSequence: s.settings.outOfSequence,
      floatFrom: s.settings.floatFrom,
      floatType: s.settings.floatType,
      lagCalendar: s.settings.lagCalendar,
      criticalBy: s.settings.criticalBy,
      criticalFloat: Math.round(s.settings.criticalFloatDays * h * 60),
      nearCriticalFloat: Math.round(s.settings.nearCriticalDays * h * 60),
      defaultCalendarId: def,
      calendars,
    },
  };
}

/** Пересчёт в браузере: даты, резервы, ведущие связи, конфликты */
export function recalcLocal(v: View): View {
  const { acts, rels, opts } = engineInput(v);
  if (!Object.keys(opts.calendars).length) return v;
  const res = computeSchedule(acts, rels, opts);
  return {
    ...v,
    schedule: {
      ...v.schedule,
      calcFinish: v.activities.length ? formatMoment(res.projectFinish) : null,
      conflicts: res.conflicts.filter((c) => v.activities.some((a) => a.id === c.activityId)),
      cycles: res.cycles,
    },
    activities: v.activities.map((a) => {
      const r = res.activities.get(a.id);
      if (!r) return a;
      return {
        ...a,
        dates: {
          es: formatMoment(r.es),
          ef: formatMoment(r.ef),
          ls: formatMoment(r.ls),
          lf: formatMoment(r.lf),
          tf: r.tf,
          ff: r.ff,
          remaining: r.remaining,
          critical: r.critical,
          nearCritical: r.nearCritical,
          longestPath: r.longestPath,
          status: r.status,
        },
      };
    }),
    relations: v.relations.map((r) => {
      const x = res.relations.get(r.id);
      return x ? { ...r, slack: x.slack, driving: x.driving } : r;
    }),
  };
}

function blankActivity(id: string, d: ActivityInput, sort: number): ActivityRow {
  return {
    id,
    wbsId: d.wbsId ?? null,
    code: d.code ?? '…',
    name: d.name ?? 'Новая работа',
    type: d.type ?? 'task',
    calendarId: d.calendarId ?? null,
    durationMin: d.durationMin ?? 0,
    remainingMin: d.remainingMin ?? null,
    durationMode: d.durationMode ?? 'fixed',
    quantity: d.quantity ?? null,
    unit: d.unit ?? null,
    ratePerDay: d.ratePerDay ?? null,
    crews: d.crews ?? null,
    constraintType: d.constraintType ?? 'asap',
    constraintDate: d.constraintDate ?? null,
    deadline: d.deadline ?? null,
    actualStart: d.actualStart ?? null,
    actualFinish: d.actualFinish ?? null,
    percent: d.percent ?? 0,
    percentType: d.percentType ?? 'duration',
    actualQuantity: d.actualQuantity ?? null,
    level: d.level ?? 'detailed',
    responsibleId: d.responsibleId ?? null,
    contractorId: d.contractorId ?? null,
    contractId: d.contractId ?? null,
    zoneId: d.zoneId ?? null,
    workType: d.workType ?? null,
    note: d.note ?? null,
    sortOrder: d.sortOrder ?? sort,
    codes: {},
    lockedBy: null,
    dates: null,
    updatedAt: new Date().toISOString(),
  };
}

/** Поля, которые сервер выводит сам: их возвращает и отмена правки */
const DERIVED: Array<keyof ActivityInput> = ['actualStart', 'percent', 'durationMin'];

/** Те же правила, что на сервере: веха без длительности, факт окончания — с началом и 100 %, длительность от объёма */
function derive(v: View, cur: ActivityRow, next: ActivityRow, d: Partial<ActivityRow>): ActivityRow {
  const out = { ...next };
  if (out.type === 'start_ms' || out.type === 'finish_ms') out.durationMin = 0;
  if (out.actualFinish && !out.actualStart) out.actualStart = out.actualFinish;
  if (d.actualFinish && d.percent === undefined) out.percent = 100;
  if (out.durationMode === 'quantity' && out.type === 'task') {
    const m = durationFromQuantity(
      out.quantity ?? 0,
      out.ratePerDay ?? 0,
      out.crews ?? 1,
      hpd(v, out.calendarId),
    );
    if (m > 0) out.durationMin = m;
  }
  void cur;
  return out;
}

/** Применить пакет к виду в браузере (до ответа сервера) */
export function applyLocal(v: View, ops: ScheduleOp[]): View {
  let acts = v.activities;
  let rels = v.relations;
  let wbs = v.wbs;
  for (const o of ops) {
    if (o.op === 'activity') {
      const i = acts.findIndex((a) => a.id === o.id);
      const d = Object.fromEntries(
        Object.entries(o.data).filter(([, x]) => x !== undefined),
      ) as Partial<ActivityRow>;
      const cur = i >= 0 ? acts[i]! : blankActivity(o.id, o.data, acts.length);
      const next = derive(v, cur, { ...cur, ...d } as ActivityRow, d);
      if (i >= 0) acts = acts.map((a, k) => (k === i ? next : a));
      else acts = [...acts, next];
    } else if (o.op === 'activity.delete') {
      acts = acts.filter((a) => a.id !== o.id);
      rels = rels.filter((r) => r.predId !== o.id && r.succId !== o.id);
    } else if (o.op === 'relation') {
      const row: RelationRow = {
        id: o.id,
        predId: o.predId,
        succId: o.succId,
        type: o.type,
        lagMin: o.lagMin,
        lagUnit: o.lagUnit ?? 'd',
        lagPct: o.lagPct ?? null,
        lagCalendar: o.lagCalendar ?? null,
        slack: null,
        driving: false,
      };
      const i = rels.findIndex((r) => r.id === o.id || (r.predId === o.predId && r.succId === o.succId));
      rels = i >= 0 ? rels.map((r, k) => (k === i ? { ...row, id: r.id } : r)) : [...rels, row];
    } else if (o.op === 'relation.delete') {
      rels = rels.filter((r) => r.id !== o.id);
    } else if (o.op === 'wbs') {
      const i = wbs.findIndex((w) => w.id === o.id);
      const d = Object.fromEntries(Object.entries(o.data).filter(([, x]) => x !== undefined));
      if (i >= 0) wbs = wbs.map((w, k) => (k === i ? { ...w, ...d } : w));
      else
        wbs = [
          ...wbs,
          {
            id: o.id,
            parentId: o.data.parentId ?? null,
            code: o.data.code ?? null,
            name: o.data.name ?? 'Новый узел',
            kind: o.data.kind ?? 'other',
            objectId: o.data.objectId ?? null,
            sectionCode: o.data.sectionCode ?? null,
            sortOrder: o.data.sortOrder ?? wbs.length,
          },
        ];
    } else if (o.op === 'wbs.delete') {
      const w = wbs.find((x) => x.id === o.id);
      wbs = wbs
        .filter((x) => x.id !== o.id)
        .map((x) => (x.parentId === o.id ? { ...x, parentId: w?.parentId ?? null } : x));
      acts = acts.map((a) => (a.wbsId === o.id ? { ...a, wbsId: w?.parentId ?? null } : a));
    } else if (o.op === 'code') {
      acts = acts.map((a) => {
        if (a.id !== o.activityId) return a;
        const codes = { ...a.codes };
        if (o.valueId) codes[o.typeId] = o.valueId;
        else delete codes[o.typeId];
        return { ...a, codes };
      });
    }
  }
  return { ...v, activities: acts, relations: rels, wbs };
}

const ACT_FIELDS: Array<keyof ActivityInput> = [
  'wbsId',
  'code',
  'name',
  'type',
  'calendarId',
  'durationMin',
  'remainingMin',
  'durationMode',
  'quantity',
  'unit',
  'ratePerDay',
  'crews',
  'constraintType',
  'constraintDate',
  'deadline',
  'actualStart',
  'actualFinish',
  'percent',
  'percentType',
  'actualQuantity',
  'level',
  'responsibleId',
  'contractorId',
  'contractId',
  'zoneId',
  'workType',
  'note',
  'sortOrder',
];

function relOp(r: RelationRow, create = true): ScheduleOp {
  return {
    op: 'relation',
    id: r.id,
    create,
    predId: r.predId,
    succId: r.succId,
    type: r.type,
    lagMin: r.lagMin,
    lagUnit: r.lagUnit,
    lagPct: r.lagPct,
    lagCalendar: r.lagCalendar,
  };
}

/** Обратный пакет: что вернуть, чтобы отменить ops (считается по виду до правки) */
export function inverseOps(v: View, ops: ScheduleOp[]): ScheduleOp[] {
  const out: ScheduleOp[] = [];
  for (const o of [...ops].reverse()) {
    if (o.op === 'activity') {
      const cur = v.activities.find((a) => a.id === o.id);
      if (!cur) {
        out.push({ op: 'activity.delete', id: o.id });
        continue;
      }
      const data: Record<string, unknown> = {};
      for (const k of [...(Object.keys(o.data) as Array<keyof ActivityInput>), ...DERIVED])
        data[k] = cur[k as keyof ActivityRow] ?? null;
      // Длительность и процент не бывают пустыми
      data.durationMin = cur.durationMin;
      data.percent = cur.percent;
      out.push({ op: 'activity', id: o.id, data: data as ActivityInput });
    } else if (o.op === 'activity.delete') {
      const cur = v.activities.find((a) => a.id === o.id);
      if (!cur) continue;
      const data: Record<string, unknown> = {};
      for (const k of ACT_FIELDS) data[k] = cur[k as keyof ActivityRow] ?? undefined;
      for (const k of Object.keys(data)) if (data[k] === null) delete data[k];
      // Работа возвращается с тем же номером и своими связями
      out.push(...v.relations.filter((r) => r.predId === o.id || r.succId === o.id).map((r) => relOp(r)));
      for (const [typeId, valueId] of Object.entries(cur.codes))
        out.push({ op: 'code', activityId: o.id, typeId, valueId });
      out.push({ op: 'activity', id: o.id, create: true, data: data as ActivityInput });
    } else if (o.op === 'relation') {
      const cur = v.relations.find((r) => r.id === o.id || (r.predId === o.predId && r.succId === o.succId));
      out.push(cur ? relOp(cur, false) : { op: 'relation.delete', id: o.id });
    } else if (o.op === 'relation.delete') {
      const cur = v.relations.find((r) => r.id === o.id);
      if (cur) out.push(relOp(cur));
    } else if (o.op === 'wbs') {
      const cur = v.wbs.find((w) => w.id === o.id);
      if (!cur) out.push({ op: 'wbs.delete', id: o.id });
      else {
        const data: Record<string, unknown> = {};
        for (const k of Object.keys(o.data)) data[k] = (cur as Record<string, unknown>)[k] ?? null;
        out.push({ op: 'wbs', id: o.id, data });
      }
    } else if (o.op === 'wbs.delete') {
      const cur = v.wbs.find((w) => w.id === o.id);
      if (!cur) continue;
      // Вернуть узел и положить в него его работы и подузлы
      const moves: ScheduleOp[] = [
        ...v.activities
          .filter((a) => a.wbsId === o.id)
          .map((a): ScheduleOp => ({ op: 'activity', id: a.id, data: { wbsId: o.id } })),
        ...v.wbs
          .filter((w) => w.parentId === o.id)
          .map((w): ScheduleOp => ({ op: 'wbs', id: w.id, data: { parentId: o.id } })),
      ];
      out.push({
        op: 'wbs',
        id: cur.id,
        create: true,
        data: {
          parentId: cur.parentId,
          code: cur.code,
          name: cur.name,
          kind: cur.kind,
          objectId: cur.objectId,
          sectionCode: cur.sectionCode,
          sortOrder: cur.sortOrder,
        },
      });
      out.push(...moves);
    } else if (o.op === 'code') {
      const cur = v.activities.find((a) => a.id === o.activityId);
      out.push({
        op: 'code',
        activityId: o.activityId,
        typeId: o.typeId,
        valueId: cur?.codes[o.typeId] ?? null,
      });
    }
  }
  // Создание узлов ИСР и работ — до ссылок на них
  const rank = (x: ScheduleOp) =>
    x.op === 'wbs' && x.create
      ? 0
      : x.op === 'activity' && x.create
        ? 1
        : x.op === 'relation' || x.op === 'code'
          ? 3
          : 2;
  return out.sort((a, b) => rank(a) - rank(b));
}

/* ───────────────────────── Строки дерева ───────────────────────── */

export type Row =
  | {
      kind: 'wbs';
      id: string;
      depth: number;
      name: string;
      code: string | null;
      start: string | null;
      finish: string | null;
      critical: boolean;
      count: number;
      collapsed: boolean;
    }
  | {
      kind: 'group';
      id: string;
      depth: number;
      name: string;
      start: string | null;
      finish: string | null;
      critical: boolean;
      count: number;
      collapsed: boolean;
    }
  | { kind: 'act'; id: string; depth: number; a: ActivityRow };

export interface RowFilter {
  search: string;
  criticalOnly: boolean;
  status: 'all' | 'not_started' | 'in_progress' | 'complete';
  level: 'all' | ActivityRow['level'];
  /** wbs — по ИСР, none — без группировки, contractor, zone, responsible, workType, level, code:<id> */
  groupBy: string;
  sort: 'es' | 'code' | 'tree';
}

export const DEFAULT_FILTER: RowFilter = {
  search: '',
  criticalOnly: false,
  status: 'all',
  level: 'all',
  groupBy: 'wbs',
  sort: 'tree',
};

const LEVEL_ORDER: ActivityRow['level'][] = ['directive', 'general', 'detailed', 'weekly'];

function matches(a: ActivityRow, f: RowFilter): boolean {
  if (f.criticalOnly && !a.dates?.critical) return false;
  if (f.status !== 'all' && a.dates?.status !== f.status) return false;
  // Уровни графика (ПЛ-28): директивный — вехи, генеральный — вехи и работы генерального уровня, и т. д.
  if (f.level !== 'all') {
    const ms = a.type === 'start_ms' || a.type === 'finish_ms';
    if (!ms && LEVEL_ORDER.indexOf(a.level) > LEVEL_ORDER.indexOf(f.level)) return false;
  }
  if (f.search) {
    const q = f.search.toLowerCase();
    if (!a.name.toLowerCase().includes(q) && !a.code.toLowerCase().includes(q)) return false;
  }
  return true;
}

function startOf(a: ActivityRow) {
  return a.actualStart ?? a.dates?.es ?? null;
}
function finishOf(a: ActivityRow) {
  return a.actualFinish ?? a.dates?.ef ?? null;
}

export function barDates(a: ActivityRow): { start: string | null; finish: string | null } {
  return { start: startOf(a), finish: finishOf(a) };
}

/** Строки таблицы и диаграммы: дерево ИСР со сводными строками или группы по коду */
/** Работы под отбор и, если задано, только из набора (трассировка логики, путь резерва) */
export function filterIds(v: View, f: RowFilter, only?: Set<string> | null): Set<string> {
  return new Set(v.activities.filter((a) => (!only || only.has(a.id)) && matches(a, f)).map((a) => a.id));
}

export function buildRows(v: View, f: RowFilter, collapsed: Set<string>, only?: Set<string> | null): Row[] {
  const acts = v.activities.filter((a) => (!only || only.has(a.id)) && matches(a, f));
  const cmp =
    f.sort === 'code'
      ? (a: ActivityRow, b: ActivityRow) => a.code.localeCompare(b.code, 'ru', { numeric: true })
      : f.sort === 'es'
        ? (a: ActivityRow, b: ActivityRow) =>
            (startOf(a) ?? '').localeCompare(startOf(b) ?? '') || a.sortOrder - b.sortOrder
        : (a: ActivityRow, b: ActivityRow) =>
            a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'ru', { numeric: true });
  const span = (list: ActivityRow[]) => {
    let s: string | null = null;
    let e: string | null = null;
    let crit = false;
    for (const a of list) {
      const x = startOf(a);
      const y = finishOf(a);
      if (x && (!s || x < s)) s = x;
      if (y && (!e || y > e)) e = y;
      if (a.dates?.critical) crit = true;
    }
    return { s, e, crit };
  };
  if (f.groupBy === 'none') return [...acts].sort(cmp).map((a) => ({ kind: 'act', id: a.id, depth: 0, a }));
  if (f.groupBy !== 'wbs') {
    const keyOf = (a: ActivityRow): [string, string] => {
      if (f.groupBy === 'contractor')
        return [
          a.contractorId ?? '',
          v.contractors.find((c) => c.id === a.contractorId)?.name ?? 'Без подрядчика',
        ];
      if (f.groupBy === 'zone')
        return [a.zoneId ?? '', v.zones.find((z) => z.id === a.zoneId)?.name ?? 'Без захватки'];
      if (f.groupBy === 'responsible')
        return [
          a.responsibleId ?? '',
          v.people.find((p) => p.id === a.responsibleId)?.name ?? 'Без ответственного',
        ];
      if (f.groupBy === 'workType') return [a.workType ?? '', a.workType ?? 'Вид работ не указан'];
      if (f.groupBy === 'level')
        return [
          a.level,
          {
            directive: 'Директивный',
            general: 'Генеральный',
            detailed: 'Детальный',
            weekly: 'Недельно-суточный',
          }[a.level],
        ];
      if (f.groupBy.startsWith('code:')) {
        const t = v.codeTypes.find((c) => c.id === f.groupBy.slice(5));
        const val = t?.values.find((x) => x.id === a.codes[t?.id ?? '']);
        return [val?.id ?? '', val ? `${val.value}${val.title ? ` — ${val.title}` : ''}` : 'Без значения'];
      }
      return ['', ''];
    };
    const groups = new Map<string, { name: string; list: ActivityRow[] }>();
    for (const a of acts) {
      const [k, name] = keyOf(a);
      const g = groups.get(k) ?? { name, list: [] };
      g.list.push(a);
      groups.set(k, g);
    }
    const out: Row[] = [];
    for (const [k, g] of [...groups].sort((x, y) =>
      x[0] === '' ? 1 : y[0] === '' ? -1 : x[1].name.localeCompare(y[1].name, 'ru'),
    )) {
      const id = `g:${k}`;
      const sp = span(g.list);
      out.push({
        kind: 'group',
        id,
        depth: 0,
        name: g.name,
        start: sp.s,
        finish: sp.e,
        critical: sp.crit,
        count: g.list.length,
        collapsed: collapsed.has(id),
      });
      if (!collapsed.has(id))
        for (const a of [...g.list].sort(cmp)) out.push({ kind: 'act', id: a.id, depth: 1, a });
    }
    return out;
  }
  // По ИСР
  const kids = new Map<string | null, typeof v.wbs>();
  const ids = new Set(v.wbs.map((w) => w.id));
  for (const w of v.wbs) {
    const p = w.parentId && ids.has(w.parentId) ? w.parentId : null;
    const l = kids.get(p) ?? [];
    l.push(w);
    kids.set(p, l);
  }
  const actsBy = new Map<string | null, ActivityRow[]>();
  for (const a of acts) {
    const k = a.wbsId && ids.has(a.wbsId) ? a.wbsId : null;
    const l = actsBy.get(k) ?? [];
    l.push(a);
    actsBy.set(k, l);
  }
  const filtering = Boolean(f.search || f.criticalOnly || f.status !== 'all' || f.level !== 'all');
  // Уровень графика (ПЛ-28): работы ниже выбранного уровня не показываются строками, но сворачиваются
  // в сводную строку ИСР — её сроки и число работ считаются по всем работам узла
  const rollBy = new Map<string | null, ActivityRow[]>();
  if (f.level !== 'all')
    for (const a of v.activities) {
      if ((only && !only.has(a.id)) || !matches(a, { ...f, level: 'all' })) continue;
      const k = a.wbsId && ids.has(a.wbsId) ? a.wbsId : null;
      const l = rollBy.get(k) ?? [];
      l.push(a);
      rollBy.set(k, l);
    }
  const under = f.level !== 'all' ? rollBy : actsBy;
  const sub = new Map<string, ActivityRow[]>();
  const collect = (id: string): ActivityRow[] => {
    const own = under.get(id) ?? [];
    const all = [...own, ...(kids.get(id) ?? []).flatMap((w) => collect(w.id))];
    sub.set(id, all);
    return all;
  };
  for (const w of kids.get(null) ?? []) collect(w.id);
  const out: Row[] = [];
  const walk = (parent: string | null, depth: number) => {
    const ws = [...(kids.get(parent) ?? [])].sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || (a.code ?? '').localeCompare(b.code ?? '', 'ru', { numeric: true }),
    );
    for (const w of ws) {
      const all = sub.get(w.id) ?? [];
      if (filtering && !all.length) continue;
      const sp = span(all);
      const c = collapsed.has(w.id);
      out.push({
        kind: 'wbs',
        id: w.id,
        depth,
        name: w.name,
        code: w.code,
        start: sp.s,
        finish: sp.e,
        critical: sp.crit,
        count: all.length,
        collapsed: c,
      });
      if (!c) walk(w.id, depth + 1);
    }
    for (const a of [...(actsBy.get(parent) ?? [])].sort(cmp)) out.push({ kind: 'act', id: a.id, depth, a });
  };
  walk(null, 0);
  return out;
}

/* ───────────────────────── Шкала ───────────────────────── */

export const ZOOMS = [
  { key: 'hours', title: 'Часы', pxDay: 240 },
  { key: 'days', title: 'Дни', pxDay: 30 },
  { key: 'weeks', title: 'Недели', pxDay: 10 },
  { key: 'months', title: 'Месяцы', pxDay: 3 },
  { key: 'quarters', title: 'Кварталы', pxDay: 1.1 },
] as const;
export type ZoomKey = (typeof ZOOMS)[number]['key'];

/** Минуты момента «ГГГГ-ММ-ДДTЧЧ:ММ» от начала шкалы → пиксели */
export function momentX(s: string, originDay: number, pxDay: number): number {
  const day = dayNumber(s);
  const hh = Number(s.slice(11, 13) || 0);
  const mm = Number(s.slice(14, 16) || 0);
  return (day - originDay + (hh * 60 + mm) / 1440) * pxDay;
}

/** Рабочий календарь для шкалы: выходные затеняются */
export function scaleCalendar(v: View, from: number, days: number): WorkCalendar | null {
  const d = calDef(v, null);
  return d ? new WorkCalendar(d, from, days) : null;
}
